import { AdminApiSchema, StaffApiSchema } from "@apiUtils/index";
import { FloatingSnackbar } from "@components/FloatingSnackbar";
import { Table } from "@components/Table";
import useOrdering from "@components/Table/useOrdering";
import { useTableCheckboxStateManager } from "@components/Table/useTableCheckboxStateManager";
import { useInstitutionContext } from "@hooks/institutionContext";
import { ThreadViewer, useThreadViewers } from "@hooks/threads/useThreadViewers";
import { useUserConfiguration } from "@hooks/useUserConfiguration";
import { Button, Chip, IconButton, Pagination, Snackbar } from "@narmi/design_system";
import { useQueryClient, useSuspenseQueries } from "@tanstack/react-query";
import { getOffset, getSelectedPage, getTotalPages, serializeQueryParams } from "@utils/api";
import { useNotificationContext } from "cerulean/src/NotificationContext";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { $adminApi, $staffApi } from "../../api";
import AutocompleteModal from "./assignment_autocomplete/AutocompleteModal";
import { ComboboxHeading } from "./assignment_autocomplete/ComboboxHeading";
import { ThreadsFilter } from "./filters/ThreadsFilter";
import styles from "./MessagesListV2.module.css";
import { MessageTabs } from "./MessageTabs";
import { NoResults } from "./NoResults";
import { getQueryParams } from "./queryParams";
import { ThreadOrdering, ThreadQuery, ThreadTab } from "./types";
import { ViewerStack } from "./ViewerStack";
import { statusToLabel } from "./threadUtil";

type Team = AdminApiSchema.components["schemas"]["Team"];
type StaffAssignment = StaffApiSchema.components["schemas"]["StaffAssignment"];

export function MessagesListV2() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { tab, ...initialFilters } = getQueryParams(searchParams);

  const { institution } = useInstitutionContext();
  const { currentUser } = useUserConfiguration();
  const userTeams = useMemo(() => currentUser.support_teams || [], [currentUser.support_teams]);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { sendNotification } = useNotificationContext();
  const { ordering, handleSortChange, determineSortIcon } = useOrdering("-last_message_at");

  const [selectedTab, setSelectedTab] = useState<ThreadTab>(() => {
    const defaultTab = "assigned_to_me";
    switch (tab) {
      case "assigned_to_my_team":
        return userTeams.length > 0 ? "assigned_to_my_team" : defaultTab;
      case null:
        return defaultTab;
      default:
        return tab as ThreadTab;
    }
  });
  const [filters, setFilters] = useState<ThreadQuery>(initialFilters);

  const [{ data: teamsData }, { data: requestTypesData }, { data: stafferData }] =
    useSuspenseQueries({
      queries: [
        $adminApi.queryOptions("get", "/v2/admin/teams/"),
        $staffApi.queryOptions("get", "/api/thread_categories/"),
        $staffApi.queryOptions("get", "/api/assignments/staffers/"),
      ],
    });

  const hasSearch = !!filters.search;
  const isSelfAssignmentTab = selectedTab === "assigned_to_me";
  const isTeamAssignmentTab = selectedTab === "assigned_to_my_team";
  const isStateTab = !isSelfAssignmentTab && !isTeamAssignmentTab;

  const threadQuery = useMemo(() => {
    return {
      ordering: [ordering as ThreadOrdering],
      ...filters,
      // if there's no search value, then certain keys of the query will be overwritten by the tab.
      // for example, if you're in the "Assigned to me" tab, we ignore any values
      // initially in the "staff_assignee" params in favor of querying with your user UUID.
      // however, if there is a search value, we ignore the selected tab and use all param-based filters.
      state: !hasSearch && isStateTab ? [selectedTab] : filters.state,
      assignee:
        !hasSearch && isTeamAssignmentTab && userTeams.length > 0 ? userTeams : filters.assignee,
      staff_assignee:
        !hasSearch && isSelfAssignmentTab && currentUser.id
          ? [currentUser.id]
          : filters.staff_assignee,
    };
  }, [
    ordering,
    filters,
    hasSearch,
    isStateTab,
    selectedTab,
    isTeamAssignmentTab,
    userTeams,
    isSelfAssignmentTab,
    currentUser.id,
  ]);

  const { data: threadData } = $staffApi.useSuspenseQuery("get", "/api/threads/", {
    params: {
      query: threadQuery,
    },
  });

  const { data: assignmentsData } = $staffApi.useSuspenseQuery("get", "/api/assignments/", {
    params: {
      query: {
        assignment_type: "support_thread",
        assignment_id: threadData.results.map((thread) => thread.id),
      },
    },
  });

  const memoizedThreadIds = useMemo(
    () => threadData.results.map((thread) => thread.id),
    [threadData]
  );
  const currentViewersByThread = useThreadViewers(memoizedThreadIds);

  const teamMap = useMemo(() => {
    const map: Record<string, Team> = {};
    teamsData.results.forEach((team: Team) => {
      map[team.uuid!] = team;
    });
    return map;
  }, [teamsData]);

  const requestTypeMap = useMemo(() => {
    const map: Record<string, string> = {};
    requestTypesData.results.forEach((type) => {
      map[type.uuid] = type.name;
    });
    return map;
  }, [requestTypesData]);

  const threadAssignmentMap = useMemo(() => {
    const map: Record<string, StaffAssignment | undefined> = {};
    threadData.results.forEach((thread) => {
      const assignment = assignmentsData.results.find(
        (assignment) => assignment.assignment_id === thread.id
      );
      map[thread.id] = assignment;
    });
    return map;
  }, [threadData, assignmentsData]);

  const threads = threadData.results;

  const { checkedItems, headerChecked, handleCheckItem, resetCheckState } =
    useTableCheckboxStateManager(threads.map((thread) => thread.id));

  const assignmentModalChildren = useMemo(
    // Memoized to prevent unnecessary re-renders of AutocompleteModal content
    () => [
      <ComboboxHeading key="individual-heading" text="Individual" />,
      <AutocompleteModal.Item key="user-unassigned" value="user-unassigned">
        No individual assignee
      </AutocompleteModal.Item>,
      ...stafferData.users.map((staffer) => (
        <AutocompleteModal.Item
          key={staffer.id}
          value={`user-${staffer.id}`}
          searchValue={staffer.full_name}
        >
          {staffer.full_name}
        </AutocompleteModal.Item>
      )),
      <ComboboxHeading key="team-heading" text="Team" />,
      <AutocompleteModal.Item key="team-unassigned" value="team-unassigned">
        No team assignee
      </AutocompleteModal.Item>,
      ...teamsData.results.map((team) => (
        <AutocompleteModal.Item key={team.uuid} value={`team-${team.uuid}`} searchValue={team.name}>
          {team.name}
        </AutocompleteModal.Item>
      )),
    ],
    [stafferData.users, teamsData.results]
  );

  const onSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ["get", "/api/threads/"],
    });
    queryClient.invalidateQueries({
      queryKey: ["get", "/api/assignments/"],
    });
    sendNotification({
      type: "success",
      text: checkedItems.length > 1 ? "Messages updated" : "Message updated.",
    });
  };

  const onError = () => {
    sendNotification({
      type: "negative",
      text: checkedItems.length > 1 ? "Failed to update messages." : "Failed to update message.",
    });
  };

  const { mutate: updateThreadBulk } = $adminApi.useMutation(
    "patch",
    "/v2/admin/threads/bulk_patch/",
    {
      onSuccess,
      onError,
    }
  );

  const { mutate: postAssignment } = $staffApi.useMutation("post", "/api/assignments/", {
    onSuccess,
    onError,
  });

  useEffect(() => {
    resetCheckState();
  }, [threads, filters, assignmentsData]);

  useEffect(() => {
    setSearchParams(serializeQueryParams({ ...threadQuery, tab: selectedTab }), { replace: true });
  }, [threadQuery, selectedTab, setSearchParams]);

  return (
    <>
      <div className="margin--bottom--l">
        <Table.Search
          initialSearchValue={filters.search}
          onDebouncedChange={(value: string) => {
            setFilters((prev) => {
              return { ...prev, search: value, offset: 0 };
            });
          }}
        />
      </div>
      <div className="margin--bottom--l">
        {hasSearch ? (
          <div>
            {threadData?.count || "0"} result{threadData?.count === 1 ? "" : "s"} for "
            {filters.search}"
          </div>
        ) : (
          <MessageTabs
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            setFilters={setFilters}
          />
        )}
      </div>
      <ThreadsFilter
        filters={filters}
        setFilters={setFilters}
        showStatusTag={hasSearch || !isStateTab}
        showTeamTag={hasSearch || !isTeamAssignmentTab}
        showStaffAssigneeTag={hasSearch || !isSelfAssignmentTab}
      />
      {threads.length ? (
        <Table>
          <Table.Header>
            <Table.HeaderCheckbox
              checked={headerChecked}
              indeterminate={checkedItems.length > 0 && !headerChecked}
              onChange={(e) => {
                handleCheckItem(
                  e.target.checked,
                  threads.map((thread) => thread.id)
                );
              }}
            />
            <Table.HeaderCell>
              {institution.institution_type === "bank" ? "Customer" : "Member"}
            </Table.HeaderCell>
            <Table.HeaderCell>Subject</Table.HeaderCell>
            {(hasSearch || !isStateTab) && <Table.HeaderCell>Status</Table.HeaderCell>}
            <Table.HeaderCell
              onClick={() => handleSortChange("last_message_at")}
              suffixIcon={determineSortIcon("last_message_at")}
            >
              Last updated
            </Table.HeaderCell>
            <Table.HeaderCell>Request type</Table.HeaderCell>
            {(hasSearch || !isSelfAssignmentTab) && <Table.HeaderCell>Assignee</Table.HeaderCell>}
            {(hasSearch || !isTeamAssignmentTab || userTeams.length > 1) && (
              <Table.HeaderCell>Team</Table.HeaderCell>
            )}
            <Table.HeaderCell>Viewing</Table.HeaderCell>
          </Table.Header>
          <Table.Body>
            {threads.map((thread) => (
              <Table.Row
                key={thread.id}
                onClick={() => {
                  navigate(`/messages/${thread.id}`, {
                    state: {
                      query: { ...threadQuery, tab: selectedTab },
                    },
                  });
                }}
              >
                <Table.Checkbox
                  checked={checkedItems.includes(thread.id)}
                  onChange={(e) => handleCheckItem(e.target.checked, thread.id)}
                />
                <Table.Cell>{thread.user?.full_name || thread.contact}</Table.Cell>
                <Table.Cell>
                  <SubjectCellWithConfidence
                    subject={thread.subject}
                    confidence={thread.confidence}
                  />
                </Table.Cell>
                {(hasSearch || !isStateTab) && (
                  <Table.Cell>{statusToLabel(thread.state)}</Table.Cell>
                )}
                <Table.Cell>
                  {DateTime.fromISO(thread.last_message_at).toLocaleString(DateTime.DATETIME_MED)}
                </Table.Cell>
                <Table.Cell>{thread.category ? requestTypeMap[thread.category] : "-"}</Table.Cell>
                {(hasSearch || !isSelfAssignmentTab) && (
                  <Table.Cell>
                    {threadAssignmentMap[thread.id]?.assignee?.full_name || "-"}
                  </Table.Cell>
                )}
                {(hasSearch || !isTeamAssignmentTab || userTeams.length > 1) && (
                  <Table.Cell>
                    {thread.assignee // on the backend "thread.assignee" is the TEAM the message is assigned to, not the user
                      ? teamMap[thread.assignee]
                        ? teamMap[thread.assignee].name
                        : "Unknown team"
                      : "-"}
                  </Table.Cell>
                )}
                <Table.Cell>
                  <ThreadViewers
                    viewers={(currentViewersByThread[thread.id] ?? []).filter(
                      (viewer) => viewer.uuid !== currentUser.id
                    )}
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      ) : (
        <NoResults tab={selectedTab} filters={threadQuery} />
      )}
      <Pagination
        totalPages={getTotalPages(filters.limit, threadData?.count ?? 0) || 1}
        selectedPage={getSelectedPage(filters.offset, filters.limit)}
        onPageChange={(page: number) =>
          setFilters({ ...filters, offset: getOffset(page, filters.limit) })
        }
      />
      {checkedItems.length > 0 && (
        <FloatingSnackbar isActive>
          <Snackbar.Text children={[`${checkedItems.length} selected`]}></Snackbar.Text>
          <IconButton
            name="x"
            onClick={() => {
              resetCheckState();
            }}
          />
          <Snackbar.Divider />
          <AutocompleteModal
            inputLabel="Assign to"
            trigger={<Button label="Assign to" />}
            onChange={(value: string) => {
              // Parse the value to determine if it's a user or team
              // assignment, then update the threads accordingly
              const match = value.match(/^([a-zA-Z]+)-(.*)$/);
              if (!match) return;
              const [, type, uuid] = match;
              if (type == "team") {
                const threadItems = checkedItems
                  .map((id) => {
                    const thread = threads.find((thread) => thread.id === id);
                    if (thread) {
                      return {
                        id: thread.id,
                        assignee: uuid === "unassigned" ? null : uuid,
                      };
                    }
                  })
                  .filter((thread) => thread !== undefined);
                updateThreadBulk({ body: threadItems });
              } else if (type == "user") {
                postAssignment({
                  body: {
                    assignee_id: uuid === "unassigned" ? null : uuid,
                    assignments: checkedItems.map((id) => ({
                      id,
                      type: "support_thread",
                      action: "assign",
                    })),
                  },
                });
              }
            }}
          >
            {assignmentModalChildren}
          </AutocompleteModal>
        </FloatingSnackbar>
      )}
    </>
  );
}

function ThreadViewers({ viewers }: { viewers: ThreadViewer[] }) {
  if (!viewers.length) {
    return "-";
  }
  return <ViewerStack viewers={viewers} />;
}

function SubjectCellWithConfidence({
  subject,
  confidence,
}: {
  subject: string | null;
  confidence: number | null;
}) {
  const { permissions, features } = useUserConfiguration();

  return (
    <>
      {features.includes("secure_messages_ai") &&
      permissions.includes("backoffice.generate_ai_message") &&
      confidence !== null ? (
        <div className={styles.subjectCellWithConfidenceContainer}>
          <span>{subject}</span>
          <ConfidenceScoreChip confidence={confidence} />
        </div>
      ) : (
        <span>{subject}</span>
      )}
    </>
  );
}

function ConfidenceScoreChip({ confidence }: { confidence: number }) {
  return confidence >= 0.8 ? (
    <div className={styles.confidenceScoreChipContainer}>
      <Chip label="High confidence" kind="success" />
      <span className="fontSize--s fontColor--secondary">draft ready</span>
    </div>
  ) : confidence >= 0.5 ? (
    <div className={styles.confidenceScoreChipContainer}>
      <Chip label="Medium confidence" kind="warn" />
      <span className="fontSize--s fontColor--secondary">draft ready</span>
    </div>
  ) : (
    <div className={styles.confidenceScoreChipContainer}>
      <Chip label="Low confidence" kind="error" />
      <span className="fontSize--s fontColor--secondary">needs review</span>
    </div>
  );
}
